/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { lazy, Suspense, type ReactElement, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

// Components
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LogoutConfirmDialog } from './logout-confirm-dialog';
const SignInFlow = lazy(() => import('./sign-in-flow'));

// Lib
import { cn } from '@/lib/utils';
import { routes } from '@/route-paths';

interface UserMenuProps {
    isAuthenticated: boolean;
    trigger: ReactElement;
}

export function UserMenu({ isAuthenticated, trigger }: UserMenuProps): ReactElement {
    const [open, setOpen] = useState(false);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [signInOpen, setSignInOpen] = useState(false);
    const [authFlowMode, setAuthFlowMode] = useState<'signIn' | 'signUp'>('signIn');
    const [hasOpenedAuthFlow, setHasOpenedAuthFlow] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const openedViaMouseRef = useRef(false);
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');

    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        openedViaMouseRef.current = true;
        setOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setOpen(false);
        }, 150); // Small delay to allow moving from trigger to content
    };

    const handleOpenChange = (newOpen: boolean) => {
        setOpen(newOpen);
        if (!newOpen) {
            if (openedViaMouseRef.current) {
                // Blur trigger when closing via mouse to prevent focus ring
                requestAnimationFrame(() => {
                    const activeElement = document.activeElement as HTMLElement;
                    if (activeElement?.closest('[data-slot="popover-trigger"]')) {
                        activeElement.blur();
                    }
                });
            }
            openedViaMouseRef.current = false;
        }
    };

    const handleOpenAutoFocus = (e: Event) => {
        if (openedViaMouseRef.current) {
            e.preventDefault();
        }
    };

    const handleCloseAutoFocus = (e: Event) => {
        if (openedViaMouseRef.current) {
            e.preventDefault();
        }
    };

    const handleMenuItemMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.focus();
    };

    const openAuthFlow = (mode: 'signIn' | 'signUp') => {
        setOpen(false);
        setAuthFlowMode(mode);
        setHasOpenedAuthFlow(true);
        setSignInOpen(true);
    };

    // Common Popover props
    const popoverContentProps = {
        className: 'w-64 p-0 overflow-hidden bg-background border-border',
        align: 'end' as const,
        sideOffset: 8,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onOpenAutoFocus: handleOpenAutoFocus,
        onCloseAutoFocus: handleCloseAutoFocus,
    };

    if (isAuthenticated) {
        const customItemClass = cn(
            'flex items-center justify-between w-full px-2 py-1 text-xs font-normal tracking-normal leading-5 text-neutral-800 rounded-md',
            'hover:bg-neutral-50 hover:text-black transition-all duration-150',
            'outline-none focus-visible:bg-neutral-50 focus-visible:text-black text-left'
        );

        return (
            <>
                <Popover open={open} onOpenChange={handleOpenChange}>
                    <PopoverTrigger
                        asChild
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onClick={(e) => {
                            e.preventDefault();
                            openedViaMouseRef.current = false;
                            setOpen((prev) => !prev);
                        }}
                    >
                        {trigger}
                    </PopoverTrigger>
                    <PopoverContent
                        {...popoverContentProps}
                        className="w-[272px] h-[178px] pt-6 pr-4 pb-6 pl-4 bg-white border-[0.5px] border-border-subtle rounded-lg shadow-none overflow-hidden"
                    >
                        <div className="flex flex-col gap-5">
                            <Link
                                to={routes.account}
                                className={customItemClass}
                                onMouseEnter={handleMenuItemMouseEnter}
                            >
                                <span>{tAccount('myAccount')}</span>
                                <ChevronRight className="h-4 w-4 text-neutral-400" />
                            </Link>
                            <Link
                                to={routes.accountOrders}
                                className={customItemClass}
                                onMouseEnter={handleMenuItemMouseEnter}
                            >
                                <span>{tAccount('myOrders')}</span>
                                <ChevronRight className="h-4 w-4 text-neutral-400" />
                            </Link>
                            <button
                                type="button"
                                className={cn(customItemClass, 'cursor-pointer')}
                                onMouseEnter={handleMenuItemMouseEnter}
                                onClick={() => {
                                    setOpen(false);
                                    setLogoutConfirmOpen(true);
                                }}
                            >
                                <span>{tAccount('navigation.logOut')}</span>
                            </button>
                        </div>
                    </PopoverContent>
                </Popover>

                <LogoutConfirmDialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen} />
            </>
        );
    }

    // Guest user menu
    return (
        <>
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger
                    asChild
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={(e) => {
                        e.preventDefault();
                        openedViaMouseRef.current = false;
                        setOpen((prev) => !prev);
                    }}
                >
                    {trigger}
                </PopoverTrigger>
                <PopoverContent {...popoverContentProps}>
                    <div className="p-4 bg-muted/50 rounded-none">
                        <p className="text-sm text-muted-foreground mb-4">{t('menu.signInForBestExperience')}</p>
                        <Button
                            type="button"
                            className="w-full mb-3 rounded-none"
                            onMouseEnter={handleMenuItemMouseEnter}
                            onClick={() => openAuthFlow('signIn')}>
                            {t('signIn')}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                            {t('menu.newCustomer')}{' '}
                            <button
                                type="button"
                                className={cn(
                                    'text-popover-foreground hover:underline rounded-none px-1 py-0.5',
                                    'outline-none focus-visible:bg-muted focus-visible:text-popover-foreground'
                                )}
                                onMouseEnter={handleMenuItemMouseEnter}
                                onClick={() => openAuthFlow('signUp')}>
                                {t('menu.createAccount')}
                            </button>
                        </p>
                    </div>
                </PopoverContent>
            </Popover>

            {hasOpenedAuthFlow && (
                <Suspense fallback={null}>
                    <SignInFlow key={authFlowMode} open={signInOpen} mode={authFlowMode} onOpenChange={setSignInOpen} />
                </Suspense>
            )}
        </>
    );
}
